import { useEffect, useRef, useState } from "react"

/**
 * Detects whether an element's content is clipped by its own size constraints —
 * horizontally (`max-width` paired with `truncate`/`text-ellipsis`) or, with
 * `axis: "vertical"`, vertically (`line-clamp-N`).
 *
 * Attach the returned `ref` to the element you want to measure. The truncation
 * state re-evaluates whenever the element resizes or its content changes, so a
 * caller can, for example, only surface a tooltip with the full text while it
 * is actually cut off.
 *
 * Pass `enabled: false` to skip measuring (the hook reports `false`) — useful
 * when the measured element only renders in certain states.
 *
 * @example
 * const { ref, isTruncated } = useIsTruncated()
 * return (
 *   <Tooltip disabled={!isTruncated}>
 *     <Tooltip.Trigger render={<span ref={ref} className="truncate">{text}</span>} />
 *     <Tooltip.Content>{text}</Tooltip.Content>
 *   </Tooltip>
 * )
 *
 * @example Multi-line clamp
 * const { ref, isTruncated } = useIsTruncated(true, "vertical")
 * return (
 *   <Tooltip disabled={!isTruncated}>
 *     <Tooltip.Trigger render={<p ref={ref} className="line-clamp-2">{text}</p>} />
 *     <Tooltip.Content>{text}</Tooltip.Content>
 *   </Tooltip>
 * )
 */
export function useIsTruncated<T extends HTMLElement = HTMLElement>(
  enabled = true,
  axis: "horizontal" | "vertical" = "horizontal"
): { ref: React.RefObject<T>; isTruncated: boolean } {
  const ref = useRef<T>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setIsTruncated(false)
      return
    }
    const element = ref.current
    if (!element) return

    // Filter observer events that don't change truncation
    let previous: boolean | undefined
    const measure = () => {
      const truncated =
        axis === "vertical"
          ? element.scrollHeight > element.clientHeight
          : element.scrollWidth > element.clientWidth
      if (truncated === previous) return
      previous = truncated
      setIsTruncated(truncated)
    }
    measure()

    // ResizeObserver catches width changes; MutationObserver catches content
    // changes that alter `scrollWidth` without resizing the (constrained) box.
    const resizeObserver = new ResizeObserver(measure)
    resizeObserver.observe(element)
    const mutationObserver = new MutationObserver(measure)
    mutationObserver.observe(element, {
      characterData: true,
      childList: true,
      subtree: true
    })

    return () => {
      resizeObserver.disconnect()
      mutationObserver.disconnect()
    }
  }, [enabled, axis])

  return { ref, isTruncated }
}
